<template>
  <fieldset
    :id="id"
    class="spot-market-rate-quote-accessorial-edit"
    tabindex="-1"
  >
    <legend class="visually-hidden">
      {{ t('common.additionalParam', { param: t('shipments.services') }) }}
    </legend>

    <Card class="no-pad">
      <template #content>
        <InputWrapper
          v-slot="{ ariaProps, inputClasses, inputId, invalid }"
          :input-id="`${id}-accessorial`"
          class="w-4 accessorial"
          :label="t('rateShop.service')"
          :errors="v$.code.$errors"
        >
          <RygenDropdown
            :id="inputId"
            v-model="v$.code.$model"
            v-bind="ariaProps"
            :class="inputClasses"
            :invalid="invalid"
            :options="availableAccessorialsWithExclusions"
            :placeholder="t('shipments.services')"
            :disabled="disabled"
            append-to="body"
            option-label="name"
            data-key="naturalId"
            filter
          />
        </InputWrapper>

        <InputWrapper
          v-slot="{ ariaProps, inputId }"
          :input-id="`${id}-stop`"
          class="w-4 stop"
          :label="t('shipments.stops')"
          optional
        >
          <RygenDropdown
            :id="inputId"
            v-model="v$.stopSequenceNumber.$model"
            v-bind="ariaProps"
            :options="parentEntity?.stops"
            :placeholder="t('shipments.stops')"
            :disabled="disabled"
            append-to="body"
            option-label="location.locationName"
            option-value="sequenceNumber"
            filter
            show-clear
          />
        </InputWrapper>

        <InputWrapper
          v-slot="{ ariaProps, inputClasses, inputId, invalid }"
          :input-id="`${id}-accessorial-charge`"
          class="w-4 charge"
          :label="t('common.charge')"
          :errors="v$.charge.$errors"
        >
          <InputNumber
            v-model="v$.charge.$model"
            v-bind="ariaProps"
            :class="inputClasses"
            :invalid="invalid"
            :input-id="inputId"
            :placeholder="t('common.charge')"
            :disabled="disabled"
            class="flex-grow-1"
            currency="USD"
            mode="currency"
            use-grouping
          />
        </InputWrapper>
      </template>

      <template #footer>
        <InlineRemoveButton
          :aria-controls="id"
          :disabled="disabled"
          @click="emit('remove', internalValue)"
        />
      </template>
    </Card>
  </fieldset>
</template>

<script lang="ts" setup>
import type { PropType, Ref } from 'vue';
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  minValue,
  required,
  useModelValue,
  useValidations,
} from '@rygen/rygen-ui/composables';
import {
  AccessorialCode,
  AccessorialCodeWellKnownNaturalId,
} from '@rygen/rygen-ui/model';

import { RateQuoteAccessorial } from '@/model';
import { useParentEntityStore } from '@/stores/ParentEntity';

import InlineRemoveButton from '@/components/ui/InlineRemoveButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import Card from 'primevue/card';
import InputNumber from 'primevue/inputnumber';

const ACCESSORIAL_CODES_TO_EXCLUDE: Set<string> = new Set([
  AccessorialCodeWellKnownNaturalId.LINEHAUL,
  AccessorialCodeWellKnownNaturalId.FUEL,
  AccessorialCodeWellKnownNaturalId.USER_LICENSE,
  AccessorialCodeWellKnownNaturalId.PASSTHROUGH,
  AccessorialCodeWellKnownNaturalId.FUM,
]);

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  modelValue: {
    type: RateQuoteAccessorial,
    default: () => new RateQuoteAccessorial(),
  },
  availableAccessorials: {
    type: Array as PropType<AccessorialCode[]>,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
});
const { id, modelValue, availableAccessorials, disabled } = toRefs(props);
const emit = defineEmits<{
  (event: 'remove', rateQuoteAccessorial: RateQuoteAccessorial): void;
  (event: 'update:model-value', newVal: RateQuoteAccessorial): void;
}>();

const { t } = useI18n();
const { internalValue } = useModelValue(modelValue, {
  emit,
  factoryMethod: (opts: Partial<RateQuoteAccessorial>) =>
    new RateQuoteAccessorial(opts),
});
const { parentEntity } = useParentEntityStore();

const rules = {
  code: {
    required: required(t('referenceData.accessorials')),
  },
  charge: {
    minValue: minValue(0.01, t('common.charge')),
    required: required(t('common.charge')),
  },
  stopSequenceNumber: {},
};
const { v$ } = useValidations(internalValue, rules);

const availableAccessorialsWithExclusions: Ref<AccessorialCode[]> = computed(
  () => {
    return availableAccessorials.value.filter(
      (accessorial) =>
        !ACCESSORIAL_CODES_TO_EXCLUDE.has(accessorial.naturalId ?? ''),
    );
  },
);
</script>

<style>
.spot-market-rate-quote-accessorial-edit {
  margin: 0;
  padding: 0;
  border: 0;

  & > .p-card {
    .p-card-body {
      display: flex;
      flex-direction: row;
      gap: 0;

      .p-card-content {
        display: flex;
        gap: var(--sp-3);
        flex-wrap: wrap;
        padding: var(--sp-3);
        width: 100%;

        .accessorial {
          flex: 2 0 100%;
        }

        .stop,
        .charge {
          flex: 1 1 auto;
        }

        .w-4 {
          /* prevent the charge input from overflowing its container */
          .p-inputtext.p-component {
            max-width: 100%;
          }
        }
      }

      .p-card-footer {
        display: flex;
        justify-content: center;
        align-items: center;
        flex: 0 0 auto;

        .rygen-button {
          height: 100%;
          background-color: var(--p-gray-100);
          border-left: var(--divider);
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;

          &:hover {
            background-color: var(--p-gray-200);
          }
        }
      }
    }
  }
}
</style>
